import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Home, FileText, BarChart3 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout, user } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path) => pathname === path;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--off-white)' }}>
      {/* Header */}
      <header style={{
        background: 'white',
        borderBottom: '1px solid var(--border-color)',
        padding: '1rem 2rem',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: '100'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h1 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: 'var(--navy)',
            margin: 0,
            cursor: 'pointer'
          }} onClick={() => navigate('/admin')}>
            Admin Dashboard
          </h1>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2rem'
          }}>
            <span style={{
              fontSize: '0.95rem',
              color: 'var(--text-secondary)'
            }}>
              Welcome, <strong>{user?.name}</strong>
            </span>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: 'none',
                color: '#c42a45',
                border: '1px solid #d96f7b',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.9rem'
              }}
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div style={{ paddingTop: '80px', display: 'flex' }}>
        {/* Sidebar */}
        <aside style={{
          width: '250px',
          background: 'white',
          borderRight: '1px solid var(--border-color)',
          padding: '2rem 0',
          minHeight: 'calc(100vh - 80px)'
        }}>
          <nav style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}>
            {/* Dashboard Link */}
            <a
              href="/admin"
              onClick={(e) => {
                e.preventDefault();
                navigate('/admin');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem 1.5rem',
                color: isActive('/admin') ? 'var(--teal)' : 'var(--text-dark)',
                background: isActive('/admin') ? 'rgba(63, 168, 174, 0.1)' : 'transparent',
                borderLeft: isActive('/admin') ? '3px solid var(--teal)' : '3px solid transparent',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                fontWeight: isActive('/admin') ? '600' : '500'
              }}
            >
              <BarChart3 size={18} />
              Dashboard
            </a>

            {/* Posts Link */}
            <a
              href="/admin/posts"
              onClick={(e) => {
                e.preventDefault();
                navigate('/admin/posts');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem 1.5rem',
                color: isActive('/admin/posts') ? 'var(--teal)' : 'var(--text-dark)',
                background: isActive('/admin/posts') ? 'rgba(63, 168, 174, 0.1)' : 'transparent',
                borderLeft: isActive('/admin/posts') ? '3px solid var(--teal)' : '3px solid transparent',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                fontWeight: isActive('/admin/posts') ? '600' : '500'
              }}
            >
              <FileText size={18} />
              Manage Posts
            </a>

            {/* Back to Site */}
            <a
              href="/blog"
              onClick={(e) => {
                e.preventDefault();
                navigate('/blog');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem 1.5rem',
                color: 'var(--text-dark)',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
            >
              <Home size={18} />
              View Blog
            </a>
          </nav>
        </aside>

        {/* Main Content */}
        <main style={{
          flex: 1,
          padding: '2rem'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
